// react
import { ReactNode, useState } from 'react';

// mui
import {
	IconButton,
	SxProps,
	TableCell,
	TableRow as MuiTableRow,
	Typography,
	Box,
} from '@mui/material';

// ui
import { TableData, TableHeader, TableProps } from '../Table';

// theme
import { theme } from '../../../../theme';

// icons
import {
	KeyboardArrowDownOutlined as IconKeyboardArrowDown,
	KeyboardArrowUpOutlined as IconKeyboardArrowUp,
} from '@mui/icons-material';

// props
export const tableRowbgColors = [
	'blue',
	'grey',
	'orange',
	'transparent',
	'white',
	'yellow',
] as const;
export type TableRowBgColor = (typeof tableRowbgColors)[number];

export interface TableRowProps {
	bgColor?: TableRowBgColor;
	btnExpandPlacement: TableProps['btnExpandPlacement'];
	cellPadding?: number;
	/** Extra empty cells to append (e.g. for column-config header cell) */
	colSpanExtra?: number;
	frame: TableProps['frame'];
	hasDividers?: boolean;
	headers: TableHeader[];
	isExpandable?: boolean;
	isStacked?: boolean;
	item: TableData;
	level?: number;
	sxTd?: SxProps;
}

export const TableRow = ({
	bgColor = 'transparent',
	btnExpandPlacement,
	cellPadding,
	colSpanExtra = 0,
	frame,
	hasDividers,
	headers,
	isExpandable,
	isStacked,
	item,
	level = 0,
	sxTd,
}: TableRowProps) => {
	// state
	const [isExpanded, setIsExpanded] = useState(false);

	// vars
	const iconExpand = isExpanded ? <IconKeyboardArrowUp /> : <IconKeyboardArrowDown />;

	const colors = {
		blue: theme.palette.brand.blue[100],
		grey: theme.palette.grey[50],
		orange: theme.palette.brand.orange[100],
		transparent: 'transparent',
		white: theme.palette.common.white,
		yellow: theme.palette.brand.yellow[100],
	};

	let sx: SxProps = {};

	if (hasDividers && !isExpanded) {
		sx = {
			...sx,
			borderBottomColor: theme.palette.divider,
			borderBottomStyle: 'solid',
			borderBottomWidth: 1,
		};
	}

	if (bgColor !== 'transparent') {
		sx = {
			...sx,
			backgroundColor: colors[bgColor],
		};
	}

	if (isStacked) {
		sx = {
			...sx,
			display: 'block',
			padding: theme.spacing(2),
			[theme.breakpoints.up('lg')]: {
				display: 'table-row',
				padding: 0,
			},
			'&:nth-child(odd)': {
				backgroundColor: theme.palette.brand.grey[100],
			},
		};
	}

	// handlers
	const handleExpand = () => {
		setIsExpanded(!isExpanded);
	};

	let childRows: ReactNode = null;

	if (item.children) {
		childRows = item.children.map((child: TableRowProps['item'], i: number) => {
			return (
				<TableRow
					bgColor={bgColor || 'transparent'}
					btnExpandPlacement={btnExpandPlacement}
					cellPadding={cellPadding}
					frame={frame}
					hasDividers={hasDividers}
					headers={headers}
					isExpandable={isExpandable}
					isStacked={isStacked}
					item={child}
					key={`table-row-${i}`}
					sxTd={sxTd}
					level={level + 1}
				/>
			);
		});
	}

	const btnExpand = (
		<TableCell
			sx={(theme) => {
				let sx: SxProps = {
					textAlign: 'left',
					width: 'auto',
				};

				if (btnExpandPlacement === 'start') {
					sx = {
						...sx,
						paddingRight: 0,
					};
				}

				if (btnExpandPlacement === 'end') {
					sx = {
						...sx,
						paddingLeft: 0,
						textAlign: 'right',
					};
				}

				if (level) {
					sx = {
						...sx,
						paddingLeft: theme.spacing(level * 3),
					};
				}

				return sx;
			}}>
			{item.children?.length || item.expandedContent ? (
				<IconButton onClick={handleExpand} size="small">
					{iconExpand}
				</IconButton>
			) : (
				<></>
			)}
		</TableCell>
	);

	return (
		<>
			<MuiTableRow sx={sx}>
				{isExpandable && btnExpandPlacement === 'start' && btnExpand}
				{headers.map((header, i) => {
					const content = header.render ? (
						header.render(item[header.value], header, item)
					) : (
						<Typography component="span">{item[header.value]}</Typography>
					);

					return (
						<TableCell
							key={`table-cell-${i}`}
							sx={(theme) => {
								let sx: SxProps = {
									padding: cellPadding,
									textAlign: 'left',
									textWrap: {
										xs: 'nowrap',
										md: 'wrap',
									},
									whiteSpace: frame === 'CARD' ? 'nowrap' : 'wrap',
									...(sxTd as object),
								};

								if (header.align) {
									sx = {
										...sx,
										textAlign: {
											lg: header.align,
										},
									};
								}

								if (isStacked) {
									sx = {
										...sx,
										display: 'grid',
										gridTemplateColumns: '1fr 1fr',
										padding: 0,
										[theme.breakpoints.up('lg')]: {
											display: 'table-cell',
											padding: theme.spacing(1, 3),
										},
									};
								}

								return sx;
							}}>
							{isStacked && (
								<Typography
									color="brand.grey.500"
									fontWeight={600}
									sx={(theme) => {
										return {
											display: {
												lg: 'none',
											},
										};
									}}
									variant="caption">
									{header.label}
								</Typography>
							)}{' '}
							{content}
						</TableCell>
					);
				})}
				{isExpandable && btnExpandPlacement === 'end' && btnExpand}
				{colSpanExtra > 0 && <TableCell colSpan={colSpanExtra} />}
			</MuiTableRow>
			{isExpanded &&
				(childRows || (
					<MuiTableRow>
						<TableCell colSpan={headers.length + 1 + colSpanExtra} sx={{ padding: 0 }}>
							<Box sx={{ backgroundColor: colors[bgColor] }}>{item.expandedContent}</Box>
						</TableCell>
					</MuiTableRow>
				))}
		</>
	);
};
